export const SET_STEP = 'AI_PROGRAM_IMPORT/SET_STEP';

// Step indices for the import wizard. Matches the StepProgressBar UI
// (Upload → Detect → Processing → Review) and the STEPS map in
// AIProgramImportLayout.
export const IMPORT_STEPS = {
  UPLOAD: 1,
  DETECT: 2,
  PROCESSING: 3,
  REVIEW: 4,
};

// Match confidence threshold below which a row is treated as "needs
// attention" in the review table. Mirrors the High band on the backend's
// MATCH_CONFIDENCE_BANDS (see node-server/models/constants.js).
export const LOW_CONFIDENCE_THRESHOLD = 90;

// Lifecycle of an Import_History row. Mirrors IMPORT_HISTORY_STATUS in
// node-server/models/constants.js exactly — do not renumber.
//   Pending (0)    — extraction finished; exerciseMatches/extractedRows are
//                    populated and the row is ready for the coach's review.
//                    Also the value the drafts list filters on (status=pending).
//   Success (1)    — coach confirmed; the program has been built.
//   Failed (2)     — the background extraction job threw (e.g. the AI provider
//                    was down) or the 10-min server-side reaper timed it out.
//                    errorMessage carries the reason.
//   Abandoned (3)  — coach discarded the draft.
//   Processing (4) — background extraction job is still running. The extract
//                    POST creates the row in this state and returns 202 before
//                    the AI work completes, so the client polls until it leaves
//                    Processing.
export const IMPORT_HISTORY_STATUS = {
  Pending: 0,
  Success: 1,
  Failed: 2,
  Abandoned: 3,
  Processing: 4,
};

// The backend marks any Processing row it reads after 10 minutes as Failed,
// discarding late results; the background watch stops at 9 minutes for this reason.
export const IMPORT_POLL_INTERVAL_MS = 3000;
export const IMPORT_POLL_TIMEOUT_MS = 5 * 60 * 1000;
export const IMPORT_SLOW_WATCH_INTERVAL_MS = 10 * 1000;
export const IMPORT_SLOW_WATCH_MAX_MS = 9 * 60 * 1000;

export const SET_FILE = 'AI_PROGRAM_IMPORT/SET_FILE';
export const DISCOVER_PROGRAMS_START = 'AI_PROGRAM_IMPORT/DISCOVER_PROGRAMS_START';
export const DISCOVER_PROGRAMS_FULFILLED = 'AI_PROGRAM_IMPORT/DISCOVER_PROGRAMS_FULFILLED';
export const DISCOVER_PROGRAMS_REJECTED = 'AI_PROGRAM_IMPORT/DISCOVER_PROGRAMS_REJECTED';
export const SET_SELECTED_PROGRAM = 'AI_PROGRAM_IMPORT/SET_SELECTED_PROGRAM';
export const EXTRACT_PROGRAM_START = 'AI_PROGRAM_IMPORT/EXTRACT_PROGRAM_START';
// Extract POST accepted (202). The background job is running; we have the
// importHistoryId to poll but no matches yet — isExtracting stays true.
export const EXTRACT_PROGRAM_PROCESSING = 'AI_PROGRAM_IMPORT/EXTRACT_PROGRAM_PROCESSING';
// Poll exceeded IMPORT_POLL_TIMEOUT_MS while still Processing. We stop polling
// and surface the "this is taking a while — check your drafts" state.
export const EXTRACT_PROGRAM_SLOW = 'AI_PROGRAM_IMPORT/EXTRACT_PROGRAM_SLOW';
export const EXTRACT_PROGRAM_FULFILLED = 'AI_PROGRAM_IMPORT/EXTRACT_PROGRAM_FULFILLED';
export const EXTRACT_PROGRAM_REJECTED = 'AI_PROGRAM_IMPORT/EXTRACT_PROGRAM_REJECTED';
export const RESET_IMPORT = 'AI_PROGRAM_IMPORT/RESET_IMPORT';
export const UPDATE_MATCH_SELECTION = 'AI_PROGRAM_IMPORT/UPDATE_MATCH_SELECTION';
export const CREATE_MATCH_EXERCISE_START = 'AI_PROGRAM_IMPORT/CREATE_MATCH_EXERCISE_START';
export const CREATE_MATCH_EXERCISE_FULFILLED = 'AI_PROGRAM_IMPORT/CREATE_MATCH_EXERCISE_FULFILLED';
export const CREATE_MATCH_EXERCISE_REJECTED = 'AI_PROGRAM_IMPORT/CREATE_MATCH_EXERCISE_REJECTED';
export const CONFIRM_IMPORT_START = 'AI_PROGRAM_IMPORT/CONFIRM_IMPORT_START';
export const CONFIRM_IMPORT_FULFILLED = 'AI_PROGRAM_IMPORT/CONFIRM_IMPORT_FULFILLED';
export const CONFIRM_IMPORT_REJECTED = 'AI_PROGRAM_IMPORT/CONFIRM_IMPORT_REJECTED';
// Quota lifecycle. The backend is the source of truth — we never derive
// allowedRemaining client-side. SET_QUOTA is dispatched any time we get a
// fresh ImportQuotaState (initial GET, post-extract refresh, or as the
// envelope on a QUOTA_EXCEEDED 403).
export const FETCH_IMPORT_QUOTA_START = 'AI_PROGRAM_IMPORT/FETCH_IMPORT_QUOTA_START';
export const FETCH_IMPORT_QUOTA_REJECTED = 'AI_PROGRAM_IMPORT/FETCH_IMPORT_QUOTA_REJECTED';
export const SET_IMPORT_QUOTA = 'AI_PROGRAM_IMPORT/SET_IMPORT_QUOTA';
export const FETCH_PENDING_IMPORTS_START = 'AI_PROGRAM_IMPORT/FETCH_PENDING_IMPORTS_START';
export const FETCH_PENDING_IMPORTS_FULFILLED = 'AI_PROGRAM_IMPORT/FETCH_PENDING_IMPORTS_FULFILLED';
export const FETCH_PENDING_IMPORTS_REJECTED = 'AI_PROGRAM_IMPORT/FETCH_PENDING_IMPORTS_REJECTED';
export const ABANDON_IMPORT_START = 'AI_PROGRAM_IMPORT/ABANDON_IMPORT_START';
export const ABANDON_IMPORT_FULFILLED = 'AI_PROGRAM_IMPORT/ABANDON_IMPORT_FULFILLED';
export const ABANDON_IMPORT_REJECTED = 'AI_PROGRAM_IMPORT/ABANDON_IMPORT_REJECTED';
export const LOAD_IMPORT_FOR_REVIEW_START = 'AI_PROGRAM_IMPORT/LOAD_IMPORT_FOR_REVIEW_START';
export const LOAD_IMPORT_FOR_REVIEW_FULFILLED = 'AI_PROGRAM_IMPORT/LOAD_IMPORT_FOR_REVIEW_FULFILLED';
export const LOAD_IMPORT_FOR_REVIEW_REJECTED = 'AI_PROGRAM_IMPORT/LOAD_IMPORT_FOR_REVIEW_REJECTED';

// Post-import success-banner signal. Set by confirmImport when the LD flag
// `aiImportHomeLanding` is ON so the Navigator can render the one-time
// green banner above the just-created program detail. Cleared when the
// coach changes programs / a 6s timer elapses / the page refreshes.
// When the flag is OFF (default until the new Home ships) this is never
// dispatched; the wizard lands the coach in Build with a toast instead.
export const SET_LAST_IMPORTED_PROGRAM_ID = 'AI_PROGRAM_IMPORT/SET_LAST_IMPORTED_PROGRAM_ID';
export const CLEAR_LAST_IMPORTED_PROGRAM_ID = 'AI_PROGRAM_IMPORT/CLEAR_LAST_IMPORTED_PROGRAM_ID';

// AI provider liveness (GET /healthcheck/ai-providers). The import pipeline
// needs BOTH Anthropic (extraction) and OpenAI (matching); `available` is true
// only when both are up. Used to warn + block the upload step when a provider
// is down. Fail open on a check error — we never block on an unknown result.
export const FETCH_AI_HEALTH_START = 'AI_PROGRAM_IMPORT/FETCH_AI_HEALTH_START';
export const FETCH_AI_HEALTH_FULFILLED = 'AI_PROGRAM_IMPORT/FETCH_AI_HEALTH_FULFILLED';
export const FETCH_AI_HEALTH_REJECTED = 'AI_PROGRAM_IMPORT/FETCH_AI_HEALTH_REJECTED';
